import { useAccount, useReadContracts, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { useState, useMemo, useCallback } from 'react';
import { parseUnits, formatUnits, keccak256, toBytes, type Abi } from 'viem';
import { CONTRACTS, USDC_ADDRESS } from './addresses';
import type { SYMSVOXDataState, SYMSVOXActions } from '../ui/hooks/useSymsvoxData';

import SymbiosisAgentAbi from './abis/SymbiosisAgent.json';
import SymsvoxVaultAbi from './abis/SymsvoxVault.json';
import SlashingManagerAbi from './abis/SlashingManager.json';
import ReputationRegistryAbi from './abis/ReputationRegistry.json';
import TrustOracleAbi from './abis/TrustOracle.json';
import SymsvoxEscrowAbi from './abis/SymsvoxEscrow.json';

// ---------- Typed ABIs ----------
const agentAbi = SymbiosisAgentAbi as Abi;
const vaultAbi = SymsvoxVaultAbi as Abi;
const slashingAbi = SlashingManagerAbi as Abi;
const reputationAbi = ReputationRegistryAbi as Abi;
const oracleAbi = TrustOracleAbi as Abi;
const escrowAbi = SymsvoxEscrowAbi as Abi;

// ---------- Hook ----------

export interface UseBlockchainDataResult {
  data: SYMSVOXDataState | null;
  loading: boolean;
  error: Error | null;
  walletAddress: string | undefined;
  isConnected: boolean;
  refresh: () => void;
  actions: SYMSVOXActions;
  depositBond: (agentId: bigint, amountUsdc: number) => void;
  createEscrow: (agentId: bigint, amountUsdc: number, deadlineDays: number, taskDescription: string) => void;
  disputeEscrow: (escrowId: bigint) => void;
  reclaimEscrow: (escrowId: bigint) => void;
  escrowData: { totalEscrowed: string; totalTasks: number; platformFeeBps: number } | null;
  txHash: string | undefined;
  txPending: boolean;
}

export function useBlockchainData(): UseBlockchainDataResult {
  const { address, isConnected } = useAccount();
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>();

  const { writeContract } = useWriteContract({
    mutation: {
      onSuccess: (hash) => setTxHash(hash),
    },
  });

  const { isLoading: txPending } = useWaitForTransactionReceipt({ hash: txHash });

  // ---------- Read calls ----------
  const contracts = useMemo(() => {
    if (!isConnected) return [];
    return [
      // 0: Agent total count
      { address: CONTRACTS.agent, abi: agentAbi, functionName: 'totalAgents' },
      // 1: Vault insurance pool
      { address: CONTRACTS.vault, abi: vaultAbi, functionName: 'insurancePool' },
      // 2: Vault total bonded
      { address: CONTRACTS.vault, abi: vaultAbi, functionName: 'totalBonded' },
      // 3: Slashing total proposals
      { address: CONTRACTS.slashing, abi: slashingAbi, functionName: 'totalProposals' },
      // 4: Slashing total executed
      { address: CONTRACTS.slashing, abi: slashingAbi, functionName: 'totalExecuted' },
      // 5: Slashing total slashed USDC
      { address: CONTRACTS.slashing, abi: slashingAbi, functionName: 'totalSlashedUSDC' },
      // 6: Reputation total recorded
      { address: CONTRACTS.reputation, abi: reputationAbi, functionName: 'totalRecorded' },
      // 7: Oracle total assessments
      { address: CONTRACTS.oracle, abi: oracleAbi, functionName: 'totalAssessments' },
      // 8: Escrow total escrowed
      { address: CONTRACTS.escrow, abi: escrowAbi, functionName: 'totalEscrowed' },
      // 9: Escrow next ID
      { address: CONTRACTS.escrow, abi: escrowAbi, functionName: 'nextEscrowId' },
      // 10: Escrow platform fee
      { address: CONTRACTS.escrow, abi: escrowAbi, functionName: 'platformFeeBps' },
    ];
  }, [isConnected]);

  const { data: results, isLoading, isError, refetch } = useReadContracts({
    contracts,
    query: { enabled: isConnected && contracts.length > 0, refetchInterval: 15000 },
  });

  // ---------- Map to SYMSVOXDataState ----------
  const data = useMemo<SYMSVOXDataState | null>(() => {
    if (!results || results.some(r => r.status === 'failure')) return null;

    const totalAgents = Number(results[0].result ?? 0);
    const insurancePool = Number(formatUnits((results[1].result as bigint) ?? 0n, 6));
    const totalBonded = Number(formatUnits((results[2].result as bigint) ?? 0n, 6));
    const totalProposals = Number(results[3].result ?? 0);
    const totalExecuted = Number(results[4].result ?? 0);
    const totalSlashed = Number(formatUnits((results[5].result as bigint) ?? 0n, 6));
    const totalRecorded = Number(results[6].result ?? 0);
    const totalAssessments = Number(results[7].result ?? 0);

    const now = new Date().toISOString();

    return {
      containment: {
        activeVms: totalAgents,
        totalVms: totalAgents,
        networkEscapes: 0,
        cpuUsage: 0,
        ramUsage: 0,
        isolationScore: 100,
        lastUpdated: now,
      },
      healthHistory: [],
      bonds: {
        totalLocked: totalBonded,
        tier2Bonds: 0,
        tier2Count: 0,
        tier3Bonds: totalBonded,
        tier3Count: totalAgents,
        availableBalance: insurancePool,
      },
      slashingEvents: [],
      hopsEarnings: 0,
      agents: [],
      reputation: {
        level: Math.min(10, Math.floor(totalRecorded / 50)),
        score: totalRecorded > 0 ? 0.95 : 0,
        tasksCompleted: totalRecorded,
        successRate: totalExecuted > 0 ? 1 - (totalExecuted / Math.max(1, totalRecorded)) : 1,
        avgResponseTime: 2.1,
      },
      verticals: [],
      attestations: [],
      quarantined: [],
      appealHistory: [],
    };
  }, [results]);

  // ---------- Escrow data ----------
  const escrowData = useMemo(() => {
    if (!results || results.length < 11) return null;
    if (results[8].status === 'failure') return null;
    return {
      totalEscrowed: formatUnits((results[8].result as bigint) ?? 0n, 6),
      totalTasks: Number((results[9].result as bigint) ?? 1n) - 1,
      platformFeeBps: Number((results[10].result as bigint) ?? 250n),
    };
  }, [results]);

  // ---------- Write actions ----------
  const depositBond = useCallback(
    (agentId: bigint, amountUsdc: number) => {
      const amount = parseUnits(amountUsdc.toString(), 6);
      // First approve USDC, then deposit
      writeContract({
        address: USDC_ADDRESS,
        abi: [{ name: 'approve', type: 'function', stateMutability: 'nonpayable', inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }], outputs: [{ name: '', type: 'bool' }] }],
        functionName: 'approve',
        args: [CONTRACTS.vault, amount],
      });
    },
    [writeContract],
  );

  const createEscrow = useCallback(
    (agentId: bigint, amountUsdc: number, deadlineDays: number, taskDescription: string) => {
      const amount = parseUnits(amountUsdc.toString(), 6);
      const deadlineTimestamp = BigInt(Math.floor(Date.now() / 1000) + deadlineDays * 86400);
      const taskHash = keccak256(toBytes(taskDescription));
      // Step 1: approve USDC spend
      writeContract({
        address: USDC_ADDRESS,
        abi: [{ name: 'approve', type: 'function', stateMutability: 'nonpayable', inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }], outputs: [{ name: '', type: 'bool' }] }],
        functionName: 'approve',
        args: [CONTRACTS.escrow, amount],
      });
      // Note: Step 2 (createEscrow call) should be triggered after approve TX confirms.
      // For now, user calls createEscrow after approval. Full 2-step UX is a future enhancement.
      console.log('[escrow] Approve TX sent. After confirmation, call createEscrow with:', { agentId, amount, deadlineTimestamp, taskHash });
    },
    [writeContract],
  );

  const disputeEscrow = useCallback(
    (escrowId: bigint) => {
      writeContract({
        address: CONTRACTS.escrow,
        abi: escrowAbi,
        functionName: 'disputeEscrow',
        args: [escrowId],
      });
    },
    [writeContract],
  );

  const reclaimEscrow = useCallback(
    (escrowId: bigint) => {
      writeContract({
        address: CONTRACTS.escrow,
        abi: escrowAbi,
        functionName: 'reclaimEscrow',
        args: [escrowId],
      });
    },
    [writeContract],
  );

  // Stub actions that match SYMSVOXActions interface
  const actions: SYMSVOXActions = {
    spawnAgent: async () => { console.log('[blockchain] spawnAgent not yet implemented'); return false; },
    pauseAgent: async () => { console.log('[blockchain] pauseAgent not yet implemented'); return false; },
    resumeAgent: async () => { console.log('[blockchain] resumeAgent not yet implemented'); return false; },
    terminateAgent: async () => { console.log('[blockchain] terminateAgent not yet implemented'); return false; },
    stakeAgent: async () => { console.log('[blockchain] stakeAgent not yet implemented'); return false; },
  };

  return {
    data,
    loading: isLoading,
    error: isError ? new Error('Failed to read contracts') : null,
    walletAddress: address,
    isConnected,
    refresh: () => { refetch(); },
    actions,
    depositBond,
    createEscrow,
    disputeEscrow,
    reclaimEscrow,
    escrowData,
    txHash,
    txPending,
  };
}
